import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useMassagerAuthContext } from '@shared/providers/MassagerAuthContext';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage, DEFAULT_FETCH_ERROR_MESSAGE } from '@/utils/apiError';
import { listVisits } from '@/services/massager/visit.service';
import type { VisitListResponse } from '@/types/massager/visit';

/**
 * React Query wrapper around GET /api/v1/massager/{site}/visits.
 * Site-scoped: clinic is resolved from the authenticated massager.
 * The caller supplies a target date (YYYY-MM-DD); pass null to skip fetching.
 *
 * `serviceType` (R2/A2) routes the read between `r_visits` (R2) and `visits`
 * (A2) on the backend. Included in the queryKey so toggling A2↔R2 refetches.
 */
export function useVisitList(targetDate: string | null, serviceType?: string | null) {
  const { clinic } = useMassagerAuthContext();
  const toast = useAppToast();

  const siteType = clinic?.site_type ?? '';

  const query = useQuery<VisitListResponse, Error>({
    queryKey: ['massager', 'visits', siteType, targetDate, serviceType ?? ''],
    queryFn: () => listVisits(siteType, targetDate!, serviceType),
    enabled: !!siteType && !!targetDate,
    placeholderData: prev => prev,
  });

  useEffect(() => {
    if (query.isError) {
      toast.error(getApiErrorMessage(query.error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isError, query.error]);

  return {
    visits: query.data?.data?.visits ?? [],
    targetDate: query.data?.data?.target_date ?? targetDate ?? '',
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    refetch: query.refetch,
  };
}
